import { EFinancingProgram } from '@if/core/types'
import { getApiClient } from '@src/services/api-client'
import { MutationFunction, useMutation, useQueryClient } from '@tanstack/react-query'
import { CreditTaskComment, FullCreditApplication } from '../../types'
import { fullCreditApplicationKeys } from '../full-credit-application/full-credit-application-api'

const BASE_URL = '/Applications'

// ============================================================================
// CREATE OPERATION
// ============================================================================

async function createComment(data: CreditTaskComment): Promise<CreditTaskComment> {
  const apiClient = getApiClient()
  const { financingProgramId, creditApplicationId, taskId } = data
  const url = `${BASE_URL}/${financingProgramId}/${creditApplicationId}/Tasks/${taskId}/Comments`
  const response = await apiClient.post<CreditTaskComment>(url, data)
  return response.data
}

export function useCreateComment(): [MutationFunction<CreditTaskComment, CreditTaskComment>, boolean] {
  const queryClient = useQueryClient()
  const { mutateAsync, isPending } = useMutation({
    mutationFn: createComment,
    onSuccess: (createdComment, params) => {
      const financingProgramId = params.financingProgramId!
      const creditApplicationId = params.creditApplicationId!
      const taskId = params.taskId!

      const queryKey = fullCreditApplicationKeys.detail(creditApplicationId, financingProgramId as EFinancingProgram)
      const currentData = queryClient.getQueryData<FullCreditApplication>(queryKey)
      if (currentData) {
        queryClient.setQueryData<FullCreditApplication>(queryKey, {
          ...currentData,
          taskComments: [...currentData.taskComments, createdComment],
          tasks: currentData.tasks.map((t) =>
            t.id === taskId ? { ...t, comments: [...(t.comments ?? []), createdComment] } : t,
          ),
        })
      }
    },
  })
  return [mutateAsync, isPending]
}

export async function createCommentAsync(data: CreditTaskComment): Promise<CreditTaskComment> {
  return createComment(data)
}

// ============================================================================
// UPDATE OPERATION
// ============================================================================

async function updateComment(data: CreditTaskComment): Promise<CreditTaskComment> {
  const apiClient = getApiClient()
  const { financingProgramId, creditApplicationId, taskId, id } = data
  const url = `${BASE_URL}/${financingProgramId}/${creditApplicationId}/Tasks/${taskId}/Comments/${id}`
  const response = await apiClient.put<CreditTaskComment>(url, data)
  return response.data
}

export function useUpdateComment(): [MutationFunction<CreditTaskComment, CreditTaskComment>, boolean] {
  const queryClient = useQueryClient()
  const { mutateAsync, isPending } = useMutation({
    mutationFn: updateComment,
    onSuccess: (updatedComment, params) => {
      const financingProgramId = params.financingProgramId!
      const creditApplicationId = params.creditApplicationId!
      const taskId = params.taskId!

      const queryKey = fullCreditApplicationKeys.detail(creditApplicationId, financingProgramId as EFinancingProgram)
      const currentData = queryClient.getQueryData<FullCreditApplication>(queryKey)
      if (currentData) {
        queryClient.setQueryData<FullCreditApplication>(queryKey, {
          ...currentData,
          taskComments: currentData.taskComments.map((c) => (c.id === updatedComment.id ? updatedComment : c)),
          tasks: currentData.tasks.map((t) =>
            t.id === taskId
              ? { ...t, comments: (t.comments ?? []).map((c) => (c.id === updatedComment.id ? updatedComment : c)) }
              : t,
          ),
        })
      }
    },
  })
  return [mutateAsync, isPending]
}

export async function updateCommentAsync(data: CreditTaskComment): Promise<CreditTaskComment> {
  return updateComment(data)
}

// ============================================================================
// DELETE OPERATION
// ============================================================================

async function deleteComment(data: CreditTaskComment): Promise<void> {
  const apiClient = getApiClient()
  const { financingProgramId, creditApplicationId, taskId, id } = data
  const url = `${BASE_URL}/${financingProgramId}/${creditApplicationId}/Tasks/${taskId}/Comments/${id}`
  await apiClient.delete(url)
}

export function useDeleteComment(): [MutationFunction<void, CreditTaskComment>, boolean] {
  const queryClient = useQueryClient()
  const { mutateAsync, isPending } = useMutation({
    mutationFn: deleteComment,
    onSuccess: (_result, params) => {
      const financingProgramId = params.financingProgramId!
      const creditApplicationId = params.creditApplicationId!
      const taskId = params.taskId!
      const commentId = params.id!

      const queryKey = fullCreditApplicationKeys.detail(creditApplicationId, financingProgramId as EFinancingProgram)
      const currentData = queryClient.getQueryData<FullCreditApplication>(queryKey)
      if (currentData) {
        queryClient.setQueryData<FullCreditApplication>(queryKey, {
          ...currentData,
          taskComments: currentData.taskComments.filter((c) => c.id !== commentId),
          tasks: currentData.tasks.map((t) =>
            t.id === taskId ? { ...t, comments: (t.comments ?? []).filter((c) => c.id !== commentId) } : t,
          ),
        })
      }
    },
  })
  return [mutateAsync, isPending]
}

export async function deleteCommentAsync(data: CreditTaskComment): Promise<void> {
  return deleteComment(data)
}

